import styled, { keyframes } from "styled-components";

// Animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

export const PageHeader = styled.div`
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--accent-light);
  display: flex;
  justify-content: space-between;
  align-items: center;
  animation: ${slideIn} 0.6s ease-out;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
`;

export const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  text-transform: uppercase;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

// "Nastavi gde si stala" hero traka
export const ContinueHero = styled.div`
  width: 100%;
  background: var(--gradient-primary);
  border-radius: 20px;
  padding: 25px 30px;
  display: flex;
  align-items: center;
  gap: 25px;
  margin-bottom: 35px;
  color: var(--surface);
  box-shadow: var(--shadow-soft);

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: stretch;
    gap: 18px;
    padding: 20px;
  }
`;

export const ContinueThumb = styled.img`
  width: 110px;
  height: 74px;
  object-fit: cover;
  border-radius: 12px;
  flex-shrink: 0;

  @media (max-width: 700px) {
    width: 100%;
    height: 140px;
  }
`;

export const ContinueInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ContinueLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  opacity: 0.85;
  margin-bottom: 6px;
`;

export const ContinueTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 10px 0;
  color: var(--surface);
`;

export const ContinueProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 420px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;

export const ContinueProgressBar = styled.div`
  flex: 1;
  height: 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.25);
  overflow: hidden;
`;

export const ContinueProgressFill = styled.div`
  height: 100%;
  background: var(--surface);
  border-radius: 10px;
  transition: width 0.5s ease;
`;

export const ContinueButton = styled.button`
  padding: 14px 28px;
  border: none;
  border-radius: 25px;
  background: var(--surface);
  color: var(--primary);
  font-family: inherit;
  font-weight: 700;
  font-size: 13px;
  text-transform: uppercase;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.3s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  @media (max-width: 700px) {
    width: 100%;
    justify-content: center;
  }
`;

// Naslov sekcije "Ostale edukacije"
export const OtherCoursesTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  text-transform: uppercase;
  margin: 50px 0 25px 0;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

export const CoursesGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  align-items: stretch;

  /* Kartice popunjavaju celu ćeliju grida — jednake širine i visine */
  & > div {
    max-width: 100%;
  }

  @media (max-width: 1350px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

export const CourseProgress = styled.div`
  margin-bottom: 20px;
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: var(--accent-light);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 8px;
`;

export const ProgressFill = styled.div`
  height: 100%;
  background: var(--gradient-primary);
  border-radius: 10px;
  transition: width 0.5s ease;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    animation: ${shimmer} 2s infinite;
  }
`;

export const ProgressText = styled.div`
  font-size: 12px;
  color: var(--text-muted);
  font-weight: 600;
  text-align: right;
`;

export const Button = styled.button`
  padding: 12px 25px;
  border: none;
  border-radius: 20px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  text-decoration: none;

  ${(props) =>
    props.$small &&
    `
    padding: 8px 16px;
    font-size: 12px;
  `}

  ${(props) =>
    props.$primary &&
    `
    background: var(--gradient-primary);
    color: var(--surface);
    
    &:hover:not(:disabled) {
      background: var(--accent-dark);
      color: var(--surface);
      transform: translateY(-2px);
    }
  `}

  ${(props) =>
    props.$secondary &&
    `
    background: transparent;
    color: var(--text-secondary);
    border: 2px solid var(--accent-light);
    
    &:hover:not(:disabled) {
      background: var(--accent-light);
      border-color: var(--accent);
      color: var(--accent);
      transform: translateY(-2px);
    }
  `}



  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;
export const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--text-muted);
  animation: ${slideIn} 0.6s ease-out 0.4s both;
`;

export const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
  opacity: 0.5;
`;

export const EmptyTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 10px;
  color: var(--text-secondary);
`;

export const EmptyDescription = styled.p`
  font-size: 16px;
  line-height: 1.5;
  max-width: 400px;
  margin: 0 auto;
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;

  p {
    margin-top: 20px;
    color: var(--text-secondary);
    font-size: 16px;
  }
`;
